// ./src/utils/api/EventGuestService.ts
//
// Event guests / attendees / team — everyone at an event is an `event_guest` row
// distinguished by `guestRole`. Mirrors the eventsmagic-api EventGuest SDL.
// `where.id` accepts one id or many — a bulk update (>1 id) may only mass-change
// RSVP status, role, star, and lifecycle status (server-enforced). Guest-group
// assignments ride on `guestGroupIds` (replace / detach-all, at most one per set).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { BULK_JOB_FIELDS } from "./BulkJobService";
import type { ServicePointRef } from "./ServicePointService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { BibItemInput, BibSortOrder, EventGuestTicketBib } from "./bibConstants";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants (mirror the eventsmagic-api event boundary)
// =============================================================================

/** Participant type. A single value per row (mirrors boundary EVENT_GUEST_ROLE). */
export const EVENT_GUEST_ROLE = {
  GUEST: "guest",
  // A ticket buyer — bought one or more tickets, may or may not attend. Not an
  // attendee by default; surfaced on the Guests page via the "Buyers only" filter.
  BUYER: "buyer",
  HOST: "host",
  CO_HOST: "co_host",
  ORGANIZER: "organizer",
  STAFF: "staff",
  VOLUNTEER: "volunteer",
  VENDOR: "vendor",
  SPEAKER: "speaker",
  PERFORMER: "performer",
  // The people travelling with a booked act (linked to the booking via eventArtistId):
  // hands-on crew and the wider entourage / guests of the act.
  CREW: "crew",
  ENTOURAGE: "entourage",
  OTHER: "other",
  // A venue's person at a voucher stall (owner, 2026-10-18): signs in to Redeem Vouchers and nothing else — NOT on the
  // team (never a responsible person, a shift's crew, a speaker, the hosts block). Listed on the Crew page only.
  VOUCHER_REDEEMER: "voucher_redeemer",
} as const;

export const EVENT_GUEST_ROLE_LABELS: Record<string, string> = {
  [EVENT_GUEST_ROLE.GUEST]: "Guest",
  [EVENT_GUEST_ROLE.BUYER]: "Buyer",
  [EVENT_GUEST_ROLE.HOST]: "Host",
  [EVENT_GUEST_ROLE.CO_HOST]: "Co-host",
  [EVENT_GUEST_ROLE.ORGANIZER]: "Organizer",
  [EVENT_GUEST_ROLE.STAFF]: "Staff",
  [EVENT_GUEST_ROLE.VOLUNTEER]: "Volunteer",
  [EVENT_GUEST_ROLE.VENDOR]: "Vendor",
  [EVENT_GUEST_ROLE.SPEAKER]: "Speaker",
  [EVENT_GUEST_ROLE.PERFORMER]: "Performer",
  [EVENT_GUEST_ROLE.CREW]: "Crew",
  [EVENT_GUEST_ROLE.ENTOURAGE]: "Entourage",
  [EVENT_GUEST_ROLE.OTHER]: "Other",
  [EVENT_GUEST_ROLE.VOUCHER_REDEEMER]: "Voucher redeemer",
};

/**
 * The crew roles — everyone who helps run the event (hosts, co-hosts, organizers,
 * staff, volunteers, speakers, performers/DJs, vendors, other). The Guests page is
 * everyone else (role = guest); the Crew page is these. Excludes `guest` AND
 * `buyer` — a buyer is neither a guest nor crew, and must not leak into the Crew list.
 */
export const TEAM_ROLES: string[] = Object.values(EVENT_GUEST_ROLE).filter(
  (r) => r !== EVENT_GUEST_ROLE.GUEST && r !== EVENT_GUEST_ROLE.BUYER && r !== EVENT_GUEST_ROLE.VOUCHER_REDEEMER
);

/**
 * Who the CREW PAGE lists and offers: the team, plus a stall's voucher redeemer (2026-10-18) — managed there (their link
 * and password), and nobody's teammate anywhere else (TEAM_ROLES feeds the team pickers).
 */
export const CREW_PAGE_ROLES: string[] = [...TEAM_ROLES, EVENT_GUEST_ROLE.VOUCHER_REDEEMER];

/** RSVP lifecycle (mirrors boundary EVENT_RSVP_STATUS). */
export const EVENT_RSVP_STATUS = {
  UNKNOWN: "unknown",
  INVITED: "invited",
  VISITED: "visited",
  PENDING: "pending",
  APPROVED: "approved",
  DENIED: "denied",
  MAYBE: "maybe",
  DECLINED: "declined",
  WAITLISTED: "waitlisted",
  CANCELED: "canceled",
} as const;

export const EVENT_RSVP_STATUS_LABELS: Record<string, string> = {
  [EVENT_RSVP_STATUS.UNKNOWN]: "Not invited",
  [EVENT_RSVP_STATUS.INVITED]: "Invited",
  [EVENT_RSVP_STATUS.VISITED]: "Visited",
  [EVENT_RSVP_STATUS.PENDING]: "Pending",
  [EVENT_RSVP_STATUS.APPROVED]: "Going",
  [EVENT_RSVP_STATUS.DENIED]: "Denied",
  [EVENT_RSVP_STATUS.MAYBE]: "Maybe",
  [EVENT_RSVP_STATUS.DECLINED]: "Declined",
  [EVENT_RSVP_STATUS.WAITLISTED]: "Waitlisted",
  [EVENT_RSVP_STATUS.CANCELED]: "Canceled",
};

/**
 * A Mantine color per RSVP status — badges, the dashboard's RSVP bar and its legend. ⚠ Every pair must stay clearly
 * apart on BOTH brands (CIE ΔE ≥ 25 — `check-brand-theme`): Invited and Visited were one colour (blue / indigo resolve
 * to the same palette) and Pending sat beside Declined (owner, 2026-10-06). Mirrored in @em/blocks `constants.ts`.
 */
export const EVENT_RSVP_STATUS_COLORS: Record<string, string> = {
  [EVENT_RSVP_STATUS.UNKNOWN]: "gray",
  [EVENT_RSVP_STATUS.INVITED]: "blue",
  [EVENT_RSVP_STATUS.VISITED]: "teal",
  [EVENT_RSVP_STATUS.PENDING]: "yellow",
  [EVENT_RSVP_STATUS.APPROVED]: "green",
  [EVENT_RSVP_STATUS.DENIED]: "red",
  [EVENT_RSVP_STATUS.MAYBE]: "grape",
  [EVENT_RSVP_STATUS.DECLINED]: "orange",
  [EVENT_RSVP_STATUS.WAITLISTED]: "lime",
  [EVENT_RSVP_STATUS.CANCELED]: "dark",
};

/**
 * The SOLID mark of each status — a stacked bar's segment and its legend dot — as `name.shade`, CHOSEN BY SEARCH over every
 * theme shade (2026-10-06, the owner: "more colorful and more contrast — green and blue are different but close"): the
 * widest smallest gap between any two statuses on both brands, every coloured status vivid (Lab chroma ≥ 35), and
 * NEIGHBOURS in the bar differing in LIGHTNESS by ≥ 12 L* — green and blue were 84 ΔE apart in hue but at the same
 * lightness (both L* 45), and two big blocks of equal lightness read as alike — and every mark visible as a dot on the
 * white card (≥ 1.5:1). Not the badge map:
 * a badge reads its colour as text on a wash, so every badge colour is a dark shade 6, and on a thin bar or a 10px dot
 * Invited / Visited / Not invited were three mid-dark tones of nearly the same depth ("it takes a lot of effort to notice
 * the difference" — owner, 2026-10-06). So the marks vary LIGHTNESS as well as hue: Visited a bright aqua, Not invited a
 * light grey (nothing has happened yet), Invited a mid blue. ⚠ Every two marks ΔE ≥ 40 on both brands
 * (`check-brand-theme`).
 */
export const EVENT_RSVP_STATUS_CHART_COLORS: Record<string, string> = {
  [EVENT_RSVP_STATUS.UNKNOWN]: "gray.3",
  [EVENT_RSVP_STATUS.INVITED]: "blue.6",
  [EVENT_RSVP_STATUS.VISITED]: "teal.5",
  [EVENT_RSVP_STATUS.PENDING]: "yellow.3",
  [EVENT_RSVP_STATUS.APPROVED]: "green.3",
  [EVENT_RSVP_STATUS.DENIED]: "red.4",
  [EVENT_RSVP_STATUS.MAYBE]: "grape.5",
  [EVENT_RSVP_STATUS.DECLINED]: "orange.7",
  [EVENT_RSVP_STATUS.WAITLISTED]: "lime.6",
  [EVENT_RSVP_STATUS.CANCELED]: "dark.6",
};

/** The CSS colour of a `name.shade` mark (`EVENT_RSVP_STATUS_CHART_COLORS`). */
export const chartColorVar = (mark: string): string => {
  const [name, shade = "6"] = mark.split(".");

  return `var(--mantine-color-${name}-${shade})`;
};

/** How the RSVP was captured (mirrors boundary RSVP_VIA). */
export const RSVP_VIA = {
  ONLINE: "online",
  SMS: "sms",
  PHONE: "phone",
  MANUAL: "manual",
  IMPORT: "import",
  API: "api",
} as const;

/** Human label per RSVP-via channel — how/where the RSVP was recorded. */
export const RSVP_VIA_LABELS: Record<string, string> = {
  [RSVP_VIA.ONLINE]: "Event website",
  [RSVP_VIA.SMS]: "SMS",
  [RSVP_VIA.PHONE]: "Phone call",
  [RSVP_VIA.MANUAL]: "Set by host",
  [RSVP_VIA.IMPORT]: "Imported",
  [RSVP_VIA.API]: "API",
};

/** Human label for how an RSVP was captured (empty when unset). */
export const getRsvpViaLabel = (via?: string | null): string => (via && RSVP_VIA_LABELS[via]) || "";

/** A door scan on a guest (mirrors boundary GUEST_SCAN_ACTION / the ticket TICKET_SCAN_ACTION). */
export const GUEST_SCAN_ACTION = {
  CHECK_IN: "check_in",
  CHECK_OUT: "check_out",
  UNDO_CHECK_IN: "undo_check_in",
  UNDO_CHECK_OUT: "undo_check_out",
} as const;

export type GuestScanAction = (typeof GUEST_SCAN_ACTION)[keyof typeof GUEST_SCAN_ACTION];

/** The host's verdict on a guest's request to attend (mirrors boundary GUEST_DECISION). */
export const GUEST_DECISION = {
  APPROVE: "approve",
  DENY: "deny",
} as const;

export type GuestDecision = (typeof GUEST_DECISION)[keyof typeof GUEST_DECISION];

/** Server cap on the host's approve/deny note (mirrors boundary MAX_DECISION_REASON). */
export const MAX_DECISION_REASON = 2000;

// =============================================================================
// GraphQL
// =============================================================================

const GUEST_FIELDS = `
  id
  accountId
  eventId
  eventTimeSlotId
  eventArtistId
  personId
  rsvpGroupId
  isGroupPrimary
  rsvpGroupSize
  guestRole
  nameTitle
  firstName
  middleName
  lastName
  nameSuffix
  email
  phone
  avatarUploadedFileId
  avatarUploadedFile { fileUrl thumbnailUrl }
  photoHiddenAt
  rsvpStatus
  isTest
  partySize
  invitedAt
  respondedAt
  lastVisitedAt
  approvedAt
  decidedByLabel
  decisionReason
  decisionReasonShared
  waitlistOfferedAt
  waitlistOfferExpiresAt
  ticketTypeId
  checkedInAt
  checkedInByUserId
  checkedInByLabel
  checkedOutAt
  checkedOutByUserId
  checkedOutByLabel
  checkedInServicePoint { id name kind removedAt }
  checkedOutServicePoint { id name kind removedAt }
  dateAttendance { eventTimeSlotId picked checkedInAt checkedInByLabel checkedOutAt checkedOutByLabel checkedInServicePoint { id name kind removedAt } checkedOutServicePoint { id name kind removedAt } dateOverride }
  attendingTimeSlots { id name startsAt endsAt }
  accessPassword
  inviteToken
  userId
  hasCrewPassword
  avatarUrl
  guestCode
  bibNumber
  ticketBibs { eventTicketId ticketCode ticketTypeName bibNumber }
  seatAssignment
  rsvpVia
  preferredLanguage
  dietaryRestrictions
  bio
  website
  socialUrl1
  socialUrl2
  socialUrl3
  jobTitle
  company
  optInForMessages
  optInForReminders
  optInForRsvpConfirmation
  notifyOfRsvp
  notifyOfTicketsPurchase
  notifyOfMessages
  notifyOfPartnerApplications
  notifyOfVolunteerApplications
  notifyOfSignupPayments
  messageToHost
  isStarred
  isAttendee
  isOverLimit
  awaitingPayment
  awaitingPaymentAmount
  awaitingPaymentCurrency
  awaitingPaymentUntil
  status
  rights
  guestGroups { id name color icon guestGroupSetId }
  person { id nameTitle firstName middleName lastName nameSuffix email phone avatarUploadedFileId bio personalWebsite socialUrl1 socialUrl2 socialUrl3 jobTitle company avatarUploadedFile { fileUrl } avatarGravatarUrl }
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query EventGuestList($filter: EventGuestFilter, $params: PaginationAndSorting) {
    eventGuestList(filter: $filter, params: $params) {
      code
      data { ${GUEST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

/**
 * ROADMAP #177 — a guest's ARRIVAL (the guest list's arrival line and its export). ⚠ Only the guest LIST asks for it
 * (`listWithArrival`): it costs a ticket read and a plan check per page, and the door console — which reads the raw
 * stamps — must not pay that on every scan. Null when the event's plan has no Analytics & reports.
 */
export const GUEST_ARRIVAL_FIELDS = `arrival { arrivedAt arrivedByLabel arrivedServicePoint { id name kind removedAt } leftAt leftByLabel leftServicePoint { id name kind removedAt } stillHere admissions admissionsIn }`;

const LIST_WITH_ARRIVAL_QUERY = gql`
  query EventGuestListWithArrival($filter: EventGuestFilter, $params: PaginationAndSorting) {
    eventGuestList(filter: $filter, params: $params) {
      code
      data { ${GUEST_FIELDS} ${GUEST_ARRIVAL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

/** How many tickets each row stands for. Like the arrival, only one page asks, the Guests list: the door console must not pay for it. */
const LIST_WITH_TICKET_COUNT_QUERY = gql`
  query EventGuestListWithTicketCount($filter: EventGuestFilter, $params: PaginationAndSorting) {
    eventGuestList(filter: $filter, params: $params) {
      code
      data { ${GUEST_FIELDS} ticketCount }
      errors { name errors warnings }
    }
  }
`;

const ARRIVALS_QUERY = gql`
  query EventGuestArrivals($params: EventGuestArrivalsInput) {
    eventGuestArrivals(params: $params) {
      code
      data { eventId eventTimeSlotId expected arrived here left notArrived percent }
      errors { name errors warnings }
    }
  }
`;

const ARRIVAL_BREAKDOWN_QUERY = gql`
  query EventGuestArrivalBreakdown($params: EventGuestArrivalBreakdownInput) {
    eventGuestArrivalBreakdown(params: $params) {
      code
      data {
        eventId
        eventTimeSlotId
        by
        guestGroupSetId
        rows { key label expected arrived here left percent }
      }
      errors { name errors warnings }
    }
  }
`;

// ROADMAP #179 — the attendance report: one server aggregate (the breakdown's rows, the headline, the quarter-hours).
const ARRIVAL_REPORT_QUERY = gql`
  query EventGuestArrivalReport($params: EventGuestArrivalReportInput) {
    eventGuestArrivalReport(params: $params) {
      code
      data {
        eventId
        eventTimeSlotId
        by
        guestGroupSetId
        bucketMinutes
        summary { expected arrived here left notArrived percent }
        rows { key label expected arrived here left percent }
        timeline { at key arrived departed }
        busiestQuarter { startsAt endsAt arrived }
        busiestHour { startsAt endsAt arrived }
        stay { people averageMinutes }
      }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query EventGuestGet($id: ID) {
    eventGuestGet(id: $id) {
      code
      data { ${GUEST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_MANY_QUERY = gql`
  query EventGuestGetMany($ids: [ID]) {
    eventGuestGetMany(ids: $ids) {
      code
      data { ${GUEST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation EventGuestCreate($params: EventGuestInput) {
    eventGuestCreate(params: $params) {
      code
      data { ${GUEST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation EventGuestUpdate($where: EventGuestWhereInput, $params: EventGuestInput) {
    eventGuestUpdate(where: $where, params: $params) {
      code
      data { ${GUEST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation EventGuestRemove($where: EventGuestWhereInput) {
    eventGuestRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

/**
 * #228 — what a scan answers with beside the row (the guest and ticket scans both): what it DID, and the door trail — a
 * value, no id (Apollo would merge the rows by it).
 */
export const SCAN_ANSWER_FIELDS = "scanOutcome admissionTrail { direction isReentry at byLabel servicePointId }";

const SCAN_MUTATION = gql`
  mutation EventGuestScan($params: EventGuestScanInput) {
    eventGuestScan(params: $params) {
      code
      data {
        ${GUEST_FIELDS}
        scanShift { outcome assignmentId shiftName startsAt endsAt rewardsIssued guestNote }
        ${SCAN_ANSWER_FIELDS}
      }
      errors { name errors warnings }
    }
  }
`;

const ASSIGN_BIBS_MUTATION = gql`
  mutation EventGuestAssignBibs($params: EventGuestAssignBibsInput) {
    eventGuestAssignBibs(params: $params) {
      code
      data { ${GUEST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// ROADMAP #171 — the door gives a party its BIB numbers in one save (all or nothing).
const SET_BIBS_MUTATION = gql`
  mutation EventGuestSetBibs($params: EventGuestSetBibsInput) {
    eventGuestSetBibs(params: $params) {
      code
      data { ${GUEST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const ASSIGN_RANDOM_GROUPS_MUTATION = gql`
  mutation EventGuestAssignRandomGroups($params: EventGuestAssignRandomGroupsInput) {
    eventGuestAssignRandomGroups(params: $params) {
      code
      data {
        eventId
        guestGroupSetId
        mode
        dryRun
        audienceTotal
        placed
        alreadyAssigned
        unplaced
        groups {
          guestGroupId
          name
          maxMembers
          assigned
          total
        }
      }
      errors { name errors warnings }
    }
  }
`;

const SET_RSVP_MUTATION = gql`
  mutation EventGuestSetRsvp($params: EventGuestSetRsvpInput) {
    eventGuestSetRsvp(params: $params) {
      code
      data { ${GUEST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const DECIDE_MUTATION = gql`
  mutation EventGuestDecide($params: EventGuestDecideInput) {
    eventGuestDecide(params: $params) {
      code
      data { ${GUEST_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const WAITLIST_OFFER_MUTATION = gql`
  mutation EventGuestWaitlistOffer($params: EventGuestWaitlistOfferInput) {
    eventGuestWaitlistOffer(params: $params) {
      code
      data { offered }
      errors { name errors warnings }
    }
  }
`;

/**
 * #62 P6c — the guest list's size and what it is EXPECTED to bring, for the wizard's invite list.
 * ⚠ ITS OWN QUERY, deliberately: `expectedHeads` is new, and a gateway that has not been recomposed rejects a whole query
 *   naming a field it does not know. On the shared stats query that would take down the dashboard and the Guests page's
 *   numbers until the recompose; here it costs only the one step, which says so.
 */
const GUEST_LIST_SIZE_QUERY = gql`
  query EventGuestListSize($params: EventGuestStatsInput) {
    eventGuestStats(params: $params) {
      code
      data {
        eventId
        totalGuests
        expectedHeads
      }
      errors { name errors warnings }
    }
  }
`;

/**
 * ROADMAP #60 — the plan's people limit as the host sees it (partylabz Free: 15). ⚠ It never says HOW MANY people are past
 * it — only that there are more (`hasHidden`).
 */
const GUEST_LIMIT_QUERY = gql`
  query EventGuestLimit($params: EventGuestLimitInput) {
    eventGuestLimit(params: $params) {
      code
      data {
        eventId
        peopleLimit
        shown
        hasHidden
        enforced
        known
      }
      errors { name errors warnings }
    }
  }
`;

const GUEST_STATS_QUERY = gql`
  query EventGuestStats($params: EventGuestStatsInput) {
    eventGuestStats(params: $params) {
      code
      data {
        eventId
        capacity
        totalGuests
        totalHeads
        occupyingHeads
        placesLeft
        placesTaken
        crewOnly
        buyersOnly
        otherNotAttending
        waitlistOfferedHeads
        byStatus { rsvpStatus guests heads notAttending }
      }
      errors { name errors warnings }
    }
  }
`;

/**
 * ROADMAP #186 b — the dashboard's Tickets sold and RSVPs cards in ONE read: counted in SQL on the server and cached there
 * per event (cleared by every write that changes it). `guests` is exactly what `eventGuestStats` answers for the event.
 */
const DASHBOARD_STATS_QUERY = gql`
  query EventDashboardStats($params: EventDashboardStatsInput) {
    eventDashboardStats(params: $params) {
      code
      data {
        eventId
        zone
        guests {
          eventId
          capacity
          totalGuests
          totalHeads
          occupyingHeads
          placesLeft
          placesTaken
          waitlistOfferedHeads
          byStatus { rsvpStatus guests heads notAttending }
        }
        ticketsSold
        perDate
        ticketTypes { ticketTypeId name sold quantityTotal }
        salesTrend { daily { at count } hourly { at count } }
        responsesTrend { daily { at count } hourly { at count } }
      }
      errors { name errors warnings }
    }
  }
`;

const ADD_CREW_FROM_USERS_MUTATION = gql`
  mutation EventGuestAddCrewFromUsers($params: EventGuestAddCrewFromUsersInput) {
    eventGuestAddCrewFromUsers(params: $params) {
      code
      data { id eventId personId userId guestRole firstName lastName email avatarUrl }
      errors { name errors warnings }
    }
  }
`;

const SET_CREW_ACCESS_MUTATION = gql`
  mutation EventGuestSetCrewAccess($params: EventGuestSetCrewAccessInput) {
    eventGuestSetCrewAccess(params: $params) {
      code
      data { token eventId hasPassword }
      errors { name errors warnings }
    }
  }
`;

const CATALOG_FIELDS = `
  code
  errors { name errors warnings }
  data {
    recommendedId
    eventCategoryCode
    messages {
      id
      label
      eventType
      tone
      body
    }
  }
`;

const INVITATION_CATALOG_QUERY = gql`
  query EventGuestInvitationCatalog($eventId: ID!, $channel: String) {
    eventGuestInvitationCatalog(eventId: $eventId, channel: $channel) { ${CATALOG_FIELDS} }
  }
`;

// Event-update SCENARIO catalog (location change, new date, thank-you, …); same shape, no auto-pick.
const UPDATE_CATALOG_QUERY = gql`
  query EventGuestUpdateCatalog($eventId: ID!, $channel: String) {
    eventGuestUpdateCatalog(eventId: $eventId, channel: $channel) { ${CATALOG_FIELDS} }
  }
`;

const MESSAGING_BALANCE_QUERY = gql`
  query EventMessagingBalance($params: EventMessagingBalanceInput) {
    eventMessagingBalance(params: $params) {
      code
      data {
        kind
        allowanceLimit
        allowanceUsed
        allowanceLeft
        credits
        totalAvailable
        unlimited
      }
      errors { name errors warnings }
    }
  }
`;

const PREVIEW_INVITATION_QUERY = gql`
  query EventGuestPreviewInvitation($params: EventGuestPreviewInvitationInput) {
    eventGuestPreviewInvitation(params: $params) {
      code
      errors { name errors warnings }
      data {
        eventGuestId
        guestName
        subject
        html
        text
      }
    }
  }
`;

// #132 — the import itself: the whole list is stored and added on the server, in the background. Answers with the job.
const IMPORT_START_MUTATION = gql`
  mutation EventGuestImportStart($params: EventGuestImportInput) {
    eventGuestImportStart(params: $params) {
      code
      errors { name errors warnings }
      data { ${BULK_JOB_FIELDS} }
    }
  }
`;

const IMPORT_PLAN_MUTATION = gql`
  mutation EventGuestImportPlan($params: EventGuestImportInput) {
    eventGuestImport(params: $params) {
      code
      errors { name errors warnings }
      data {
        eventId
        created
        matched
        skipped
        attached
        linked
        plan { index outcome eventGuestId leadName leadSize linked }
      }
    }
  }
`;

const SEND_RESULT_FIELDS = `
  code
  errors { name errors warnings }
  data {
    eventId
    dryRun
    recipientsTotal
    withEmail
    withoutEmail
    sent
    failed
    tokensMinted
    costEmails
    costMessages
    costByZone { zone recipients }
    countsPerRecipient
    expensiveCharacter
  }
`;

// #62 P6c Phase B — the dry run's family count, on its OWN operation: `linkedAdults` is new, and an un-recomposed gateway
// rejects a whole operation that names an unknown field. On the shared result above it would take every send down with
// it; here a failure only drops one line of the preview.
const SEND_LINKED_PREVIEW_MUTATION = gql`
  mutation EventGuestSendInvitationsLinked($params: EventGuestSendInvitationsInput) {
    eventGuestSendInvitations(params: $params) { code data { linkedAdults } errors { name errors warnings } }
  }
`;

const SEND_UPDATE_LINKED_PREVIEW_MUTATION = gql`
  mutation EventGuestSendUpdateLinked($params: EventGuestSendInvitationsInput) {
    eventGuestSendUpdate(params: $params) { code data { linkedAdults } errors { name errors warnings } }
  }
`;

const SEND_INVITATIONS_MUTATION = gql`
  mutation EventGuestSendInvitations($params: EventGuestSendInvitationsInput) {
    eventGuestSendInvitations(params: $params) { ${SEND_RESULT_FIELDS} }
  }
`;

// Event updates reuse the invitation send input + result shape (host-authored message, neutral
// template, NO rsvp-status change) — see eventGuestSendUpdate.
const SEND_UPDATE_MUTATION = gql`
  mutation EventGuestSendUpdate($params: EventGuestSendInvitationsInput) {
    eventGuestSendUpdate(params: $params) { ${SEND_RESULT_FIELDS} }
  }
`;

// Ensure + return a guest's personal access token so the host can copy their RSVP link
// (cross-device restore, host-issued path).
const ENSURE_INVITE_LINK_MUTATION = gql`
  mutation EventGuestEnsureInviteLink($eventGuestId: ID!) {
    eventGuestEnsureInviteLink(eventGuestId: $eventGuestId) {
      code
      errors { name errors warnings }
      data { token eventId }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventGuest schema)
// =============================================================================

/** A guest's assigned group (lite — for a colored icon badge + label). */
export interface EventGuestGroupRef {
  id?: string;
  name?: string | null;
  color?: string | null;
  icon?: string | null;
  guestGroupSetId?: string | null;
}

/** The linked contact (live reference). Name/contact on the guest row override these. */
export interface EventGuestPersonRef {
  id?: string;
  nameTitle?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  nameSuffix?: string | null;
  email?: string | null;
  phone?: string | null;
  avatarUploadedFileId?: string | null;
  bio?: string | null;
  personalWebsite?: string | null;
  socialUrl1?: string | null;
  socialUrl2?: string | null;
  socialUrl3?: string | null;
  /** Selected for name badges (B2 #18) — all four already existed on the Person SDL, unused by this
   *  query. ⚠ `avatarUploadedFileId` above is the raw id and cannot be printed; `personAvatarUrl` reads
   *  the RESOLVED file url and the server-computed Gravatar fallback. */
  jobTitle?: string | null;
  company?: string | null;
  avatarUploadedFile?: { fileUrl?: string | null } | null;
  avatarGravatarUrl?: string | null;
}

/** ROADMAP A4 — an RSVP guest's state on one date of a recurring event. */
export interface EventGuestDateAttendance {
  eventTimeSlotId?: string | null;
  /** Whether the guest booked this date. */
  picked?: boolean | null;
  checkedInAt?: string | null;
  checkedInByLabel?: string | null;
  checkedOutAt?: string | null;
  checkedOutByLabel?: string | null;
  /** #169 — where the check-in / check-out was scanned (null = not recorded). */
  checkedInServicePoint?: ServicePointRef | null;
  checkedOutServicePoint?: ServicePointRef | null;
  /** Crew admitted the guest although the date was not today or not one they booked. */
  dateOverride?: boolean | null;
}

/** One date a guest is attending on a recurring event (EventGuest.attendingTimeSlots). */
export interface EventGuestTimeSlot {
  id?: string | null;
  name?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
}

/** What a door scan did about a volunteer's shift (#A4.6 — one scanner at the door). Mirrors the server's SHIFT_SCAN_OUTCOME. */
export const GUEST_SCAN_SHIFT_OUTCOME = {
  CHECKED_IN: "checked_in",
  NONE_NOW: "none_now",
  ALL_CHECKED_IN: "all_checked_in",
} as const;

/** #228 — one movement through the door, on a scan's answer only (`admissionTrail`, oldest first). */
export interface AdmissionMove {
  /** "in" | "out". */
  direction?: string | null;
  /** An IN that re-admitted somebody who had checked out. */
  isReentry?: boolean | null;
  at?: string | null;
  byLabel?: string | null;
  servicePointId?: string | null;
}

export interface EventGuestScanShift {
  outcome?: string | null;
  assignmentId?: string | null;
  shiftName?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  rewardsIssued?: number | null;
  /** Why the guest rail itself would not admit them — they were admitted for their shift. */
  guestNote?: string | null;
}

export interface EventGuest {
  id?: string;
  accountId?: string | null;
  eventId?: string | null;
  eventTimeSlotId?: string | null;
  /** The booked act this guest travels with (crew / entourage / performing member). */
  eventArtistId?: string | null;
  personId?: string | null;
  rsvpGroupId?: string | null;
  isGroupPrimary?: boolean | null;
  rsvpGroupSize?: number | null;
  guestRole?: string | null;
  nameTitle?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  nameSuffix?: string | null;
  email?: string | null;
  phone?: string | null;
  avatarUploadedFileId?: string | null;
  /** The guest's own photo for this event, resolved (#69). Read through `guestPhotoUrl`. */
  avatarUploadedFile?: { fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  /** #69 — when the guest removed every photo of themselves from the event site (their own RSVP). While set the
   *  server refuses a new photo from anyone else; only the guest can show it again. */
  photoHiddenAt?: string | null;
  rsvpStatus?: string | null;
  /** ROADMAP #188 — made by a test run (the event was in test mode); hidden once the event is live. */
  isTest?: boolean | null;
  partySize?: number | null;
  invitedAt?: string | null;
  respondedAt?: string | null;
  /** When the guest last opened their personal invite link (?inv=<token>). */
  lastVisitedAt?: string | null;
  /** When the approve/deny decision was made (rsvpStatus says which way it went). */
  approvedAt?: string | null;
  /** Name snapshot of the crew member who approved/denied. */
  decidedByLabel?: string | null;
  /** The host's note on the approve/deny decision. */
  decisionReason?: string | null;
  /** Whether decisionReason was sent to the guest (false = internal crew note). */
  decisionReasonShared?: boolean | null;
  /**
   * ROADMAP #38 — a user-backed crew member's avatar, resolved LIVE from ms_auth on read (never
   * stored), so the roster shows their current picture rather than one frozen at add time.
   */
  avatarUrl?: string | null;
  /** ROADMAP #36 — when a freed place was offered to this waitlisted guest. */
  waitlistOfferedAt?: string | null;
  /** ROADMAP #36 — when that offer lapses and the place passes to the next in line. */
  waitlistOfferExpiresAt?: string | null;
  /**
   * ROADMAP #36 ticketed rail — which ticket type a waitlisted guest is queuing for. Null on the
   * RSVP rail and on an event-wide ticketed queue.
   */
  ticketTypeId?: string | null;
  checkedInAt?: string | null;
  checkedInByUserId?: string | null;
  /** Name snapshot of who checked the guest in (door-console evidence). */
  checkedInByLabel?: string | null;
  checkedOutAt?: string | null;
  checkedOutByUserId?: string | null;
  /** Name snapshot of who checked the guest out. */
  checkedOutByLabel?: string | null;
  /** #169 — where the check-in / check-out was scanned (null = not recorded). */
  checkedInServicePoint?: ServicePointRef | null;
  checkedOutServicePoint?: ServicePointRef | null;
  /**
   * ROADMAP A4: this guest's state on ONE date of a recurring event. Set only when the list was asked for a
   * date (`attendanceForTimeSlotId`) and on a scan of a recurring event; null otherwise.
   */
  dateAttendance?: EventGuestDateAttendance | null;
  /**
   * ROADMAP #177 — only on `listWithArrival`: the first arrival, "n of m in", when they left. Null without Analytics &
   * reports; ABSENT (undefined) on every other read — a mutation's answer does not carry it.
   */
  arrival?: EventGuestArrival | null;
  /**
   * How many tickets this row stands for: a buyer's, what their orders bought; anyone else's, those assigned to them. Only
   * on `listWithTicketCount`; ABSENT (undefined) on every other read, a mutation's answer included.
   */
  ticketCount?: number | null;
  /** Only on the result of a door scan: what it did about this person's volunteer shift (null = not a volunteer here). */
  scanShift?: EventGuestScanShift | null;
  /** #228 — set ONLY on eventGuestScan's answer: the door trail of what was scanned (their row, or the date's). */
  admissionTrail?: AdmissionMove[] | null;
  /** #228 — set ONLY on eventGuestScan's answer: what it did (SCAN_OUTCOME in utils/servicePoints). */
  scanOutcome?: string | null;
  /** Bug #1: the dates this guest is attending on a recurring event (RSVP picks ∪ confirmed tickets' dates), earliest first. */
  attendingTimeSlots?: EventGuestTimeSlot[] | null;
  accessPassword?: string | null;
  /** Owner-only: shareable crew invite token (build the /crew-access?inv=<token> link). */
  inviteToken?: string | null;
  /** Owner-only: linked ms_auth user id (cross-account crew access). */
  userId?: string | null;
  /** Owner-only: whether a crew sign-in password is set. */
  hasCrewPassword?: boolean | null;
  guestCode?: string | null;
  /** The guest's OWN BIB number(s), comma-separated. Every number they carry is these plus `ticketBibs` (`guestAllBibs`). */
  bibNumber?: string | null;
  /** ROADMAP #171 — the numbers on their live tickets, derived by the server on every read (never copied here). */
  ticketBibs?: EventGuestTicketBib[] | null;
  seatAssignment?: string | null;
  rsvpVia?: string | null;
  preferredLanguage?: string | null;
  dietaryRestrictions?: string | null;
  /** Event-specific bio / blurb (falls back to the linked contact's bio). */
  bio?: string | null;
  /** Event-specific website + social links (fall back to the linked contact's). */
  website?: string | null;
  socialUrl1?: string | null;
  socialUrl2?: string | null;
  socialUrl3?: string | null;
  /** Event-specific job title / company for the event website (fall back to the linked contact's). */
  jobTitle?: string | null;
  company?: string | null;
  optInForMessages?: boolean | null;
  optInForReminders?: boolean | null;
  optInForRsvpConfirmation?: boolean | null;
  notifyOfRsvp?: boolean | null;
  notifyOfTicketsPurchase?: boolean | null;
  notifyOfMessages?: boolean | null;
  /** Crew: email me when a sponsor applies. ⚠ Its own opt-in, separate from ticket purchases. */
  notifyOfPartnerApplications?: boolean | null;
  /** Crew: email me when somebody signs up to volunteer, or a volunteer withdraws (#A4.6). */
  notifyOfVolunteerApplications?: boolean | null;
  /** Crew: email me when a guest pays for sign-up list items (#149) — who bought what, how many, their notes. */
  notifyOfSignupPayments?: boolean | null;
  /** The guest's own note to the host, left at RSVP time. */
  messageToHost?: string | null;
  isStarred?: boolean | null;
  /** Counts as a head at the event (headcount). Guests always true; crew optional. Drives the Guests list. */
  isAttendee?: boolean | null;
  /** RSVP kept but hidden/uncounted — over the plan's attendee limit (server-computed). */
  isOverLimit?: boolean | null;
  /**
   * #40, server-computed. The host approved this guest back after refusing them, and their restored
   * place is HELD but not yet paid for.
   *
   * ⚠ They sit at `rsvpStatus: 'pending'` — that is what withholds their QR, their wallet pass and
   * their entry at the door — but they are NOT waiting on the host, so they must never be shown as
   * "Awaiting review". Show "Awaiting payment" instead. The only status change the API accepts for
   * them is a denial, which cancels the wait and releases the seat.
   */
  awaitingPayment?: boolean | null;
  /** #40. What is still owed on the restored place. Null unless awaitingPayment. */
  awaitingPaymentAmount?: number | null;
  /** #40. The currency `awaitingPaymentAmount` is in — the order's. Null unless awaitingPayment. */
  awaitingPaymentCurrency?: string | null;
  /** #40. When the held place is released if it goes unpaid. Null unless awaitingPayment. */
  awaitingPaymentUntil?: string | null;
  status?: number | null;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  /** The guest groups this guest is assigned to (at most one per set). */
  guestGroups?: EventGuestGroupRef[] | null;
  /** The linked contact (live). Effective name/contact = guest.<field> ?? person.<field>. */
  person?: EventGuestPersonRef | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface EventGuestInput {
  /**
   * ROADMAP A4: the dates of a recurring event this guest is booked onto — REPLACES them. The server refuses a newly
   * added date that is full and voids a dropped date's unused RSVP coupons. Send only when the dates changed.
   */
  timeSlotIds?: string[] | null;
  /** Required on create; the parent event. */
  eventId?: string | null;
  eventTimeSlotId?: string | null;
  /** Link this guest to a booked act as its touring party (crew / entourage / performer). */
  eventArtistId?: string | null;
  personId?: string | null;
  rsvpGroupId?: string | null;
  isGroupPrimary?: boolean | null;
  rsvpGroupSize?: number | null;
  guestRole?: string | null;
  nameTitle?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  nameSuffix?: string | null;
  email?: string | null;
  phone?: string | null;
  avatarUploadedFileId?: string | null;
  rsvpStatus?: string | null;
  partySize?: number | null;
  invitedAt?: string | null;
  respondedAt?: string | null;
  approvedAt?: string | null;
  accessPassword?: string | null;
  bibNumber?: string | null;
  seatAssignment?: string | null;
  rsvpVia?: string | null;
  preferredLanguage?: string | null;
  dietaryRestrictions?: string | null;
  /** Event-specific bio / blurb (falls back to the linked contact's bio). */
  bio?: string | null;
  /** Event-specific website + social links (fall back to the linked contact's). */
  website?: string | null;
  socialUrl1?: string | null;
  socialUrl2?: string | null;
  socialUrl3?: string | null;
  /** Event-specific job title / company for the event website (fall back to the linked contact's). */
  jobTitle?: string | null;
  company?: string | null;
  optInForMessages?: boolean | null;
  optInForReminders?: boolean | null;
  optInForRsvpConfirmation?: boolean | null;
  notifyOfRsvp?: boolean | null;
  notifyOfTicketsPurchase?: boolean | null;
  notifyOfMessages?: boolean | null;
  /** Crew: email me when a sponsor applies. ⚠ Its own opt-in, separate from ticket purchases. */
  notifyOfPartnerApplications?: boolean | null;
  /** Crew: email me when somebody signs up to volunteer, or a volunteer withdraws (#A4.6). */
  notifyOfVolunteerApplications?: boolean | null;
  /** Crew: email me when a guest pays for sign-up list items (#149) — who bought what, how many, their notes. */
  notifyOfSignupPayments?: boolean | null;
  /** The guest's own note to the host. */
  messageToHost?: string | null;
  isStarred?: boolean | null;
  /** Counted head at the event. Guests always true; crew optional. */
  isAttendee?: boolean | null;
  status?: number | null;
  /** Replace the guest's group assignments (at most one per set). [] detaches all; omit to leave unchanged. */
  guestGroupIds?: string[] | null;
}

/** A bulk update (>1 id) may only mass-change these fields server-side. */
export interface EventGuestBulkInput {
  rsvpStatus?: string | null;
  guestRole?: string | null;
  isStarred?: boolean | null;
  isAttendee?: boolean | null;
  partySize?: number | null;
  status?: number | null;
  /** Bulk RSVP-group assignment (assign many at once). */
  rsvpGroupId?: string | null;
  isGroupPrimary?: boolean | null;
  /** Bulk group assignment — set-scoped (replaces only the given sets, never wipes others). */
  guestGroupIds?: string[] | null;
  /** Bulk edit only: detach these group SETS from every selected guest. */
  clearGroupSetIds?: string[] | null;
}

/** One RSVP status and what it holds — see EventGuestStats. */
export interface EventGuestStatsRow {
  rsvpStatus?: string | null;
  /** Attendee ROWS in this status (is_attendee = true). */
  guests?: number | null;
  /** Attendee HEADS (SUM of party_size) — one row can be a family. */
  heads?: number | null;
  /** Rows in this status holding NO place (is_attendee = false) — crew, buyers and withdrawals. */
  notAttending?: number | null;
}

/**
 * Guest-list statistics. THREE totals, deliberately distinct:
 *  - `totalGuests`   — people ON the list (is_attendee = true). What a plan limit would cap.
 *  - `totalHeads`    — what they add up to once party sizes count.
 *  - `occupyingHeads`— the subset whose status actually holds a place (approved + pending). The
 *                      only one `capacity` is measured against.
 *
 * ⚠ `is_attendee` decides who holds a place, never the role: a crew member who is ALSO attending has
 * it set and counts like any other guest. But the reverse does NOT hold — `is_attendee = false` is
 * not "crew": buyers and withdrawn guests are false too, which is why the non-attendees are split by
 * role into crewOnly / buyersOnly / otherNotAttending.
 */
export interface EventGuestStats {
  eventId?: string | null;
  capacity?: number | null;
  totalGuests?: number | null;
  totalHeads?: number | null;
  occupyingHeads?: number | null;
  /**
   * #62 P6c — how many people the list is EXPECTED to bring: an invitation not answered yet at its group size, a reply
   * holding a place at the party it said, a no / maybe / waitlisted guest as 0. (Server rule: `expectedHeadsOf`.)
   */
  expectedHeads?: number | null;
  /** Crew ROLES not attending. A crew member who IS attending counts in totalGuests instead. */
  crewOnly?: number | null;
  /** Buyers who bought for other people and are not attending themselves. */
  buyersOnly?: number | null;
  /** Any other non-attendee — usually a guest who withdrew. */
  otherNotAttending?: number | null;
  /** Places still free, counted as the checkout and the waitlist count them. Null = uncapped. */
  placesLeft?: number | null;
  /**
   * #134 — places TAKEN, from the same count: guest heads holding a place or sold admissions, whichever is higher. On a
   * headcount ticketed event the tickets are the only count (`occupyingHeads` reads 0 there). Null = uncapped, or the
   * event repeats (capacity is per date).
   */
  placesTaken?: number | null;
  /**
   * #36 — heads among the waitlisted who are ALREADY holding a live offer and deciding. `byStatus`
   * says how many are queuing; this says how many of them have been made room for already, so a host
   * does not free the same place twice. 0 when the event runs no waitlist.
   */
  waitlistOfferedHeads?: number | null;
  byStatus?: EventGuestStatsRow[] | null;
}

/** #186 b — one bucket of a dashboard trend: `at` is the day (`YYYY-MM-DD`) or the hour (`YYYY-MM-DD HH`) on the event's clock. */
export interface EventDashboardTrendPoint {
  at: string;
  count: number;
}

/** #186 b — a trend's buckets over the last 32 days and 26 hours (only those with something in them). */
export interface EventDashboardTrend {
  daily: EventDashboardTrendPoint[];
  hourly: EventDashboardTrendPoint[];
}

/** #186 b — one live admission type (add-ons are not admissions) and what of it was sold. */
export interface EventDashboardTicketType {
  ticketTypeId: string;
  name?: string | null;
  sold: number;
  /** The type's limit; null when it has none, or on a repeating event (per date — `perDate`). */
  quantityTotal?: number | null;
}

/** ROADMAP #186 b — the event dashboard's numbers (`EventGuestService.dashboardStats`). */
export interface EventDashboardStats {
  eventId: string;
  /** The zone the trends are counted on — the event's own (UTC when it has none). */
  zone: string;
  guests: EventGuestStats | null;
  /** Confirmed admissions sold, every type together (add-ons excluded). */
  ticketsSold: number;
  /** The event repeats: its ticket limits are per date, so no type carries an "of N". */
  perDate: boolean;
  ticketTypes: EventDashboardTicketType[];
  salesTrend: EventDashboardTrend | null;
  responsesTrend: EventDashboardTrend | null;
}

/** ROADMAP #177 — the guest list's Arrival filter (mirrors the API's GUEST_ARRIVAL). */
export const GUEST_ARRIVAL = {
  NOT_ARRIVED: "not_arrived",
  ARRIVED: "arrived",
  HERE: "here",
  LEFT: "left",
} as const;

export type GuestArrivalFilter = (typeof GUEST_ARRIVAL)[keyof typeof GUEST_ARRIVAL];

/** ROADMAP #177 — a guest's arrival (`EventGuest.arrival`). */
export interface EventGuestArrival {
  arrivedAt?: string | null;
  arrivedByLabel?: string | null;
  arrivedServicePoint?: ServicePointRef | null;
  /** Set once nothing they came in on is still in. */
  leftAt?: string | null;
  leftByLabel?: string | null;
  leftServicePoint?: ServicePointRef | null;
  stillHere?: boolean | null;
  /** Their live admission tickets (0 on an RSVP event) and how many came in — "2 of 2 in". */
  admissions?: number | null;
  admissionsIn?: number | null;
}

/** ROADMAP #177 — "Arrived N of M (P%)", in people. */
export interface EventGuestArrivals {
  eventId?: string | null;
  eventTimeSlotId?: string | null;
  expected?: number | null;
  arrived?: number | null;
  here?: number | null;
  left?: number | null;
  notArrived?: number | null;
  percent?: number | null;
}

/** ROADMAP #178 — what the arrivals breakdown splits people by (mirrors the API's GUEST_ARRIVAL_BREAKDOWN). */
export const GUEST_ARRIVAL_BREAKDOWN = {
  POINT: "point",
  TICKET_TYPE: "ticket_type",
  GROUP: "group",
} as const;

export type GuestArrivalBreakdownBy = (typeof GUEST_ARRIVAL_BREAKDOWN)[keyof typeof GUEST_ARRIVAL_BREAKDOWN];

/** One row of the breakdown, in people; `key` null = everybody else (no entrance recorded, no ticket type, no group). */
export interface EventGuestArrivalBreakdownRow {
  key?: string | null;
  label?: string | null;
  /** Null per entrance (somebody who has not come has none). */
  expected?: number | null;
  arrived?: number | null;
  here?: number | null;
  left?: number | null;
  percent?: number | null;
}

export interface EventGuestArrivalBreakdown {
  eventId?: string | null;
  eventTimeSlotId?: string | null;
  by?: GuestArrivalBreakdownBy | null;
  guestGroupSetId?: string | null;
  rows?: EventGuestArrivalBreakdownRow[] | null;
}

/** ROADMAP #179 — one quarter-hour of one breakdown row (`key`), in people; `at` = its start (ISO). */
export interface EventGuestArrivalTimelinePoint {
  at: string;
  key?: string | null;
  arrived?: number | null;
  /** Left during it — counted in the row they ARRIVED in. */
  departed?: number | null;
}

export interface EventGuestArrivalWindow {
  startsAt: string;
  endsAt: string;
  arrived?: number | null;
}

/** ROADMAP #179 — the attendance report (Analytics › Attendance). */
export interface EventGuestArrivalReport extends EventGuestArrivalBreakdown {
  bucketMinutes?: number | null;
  /** The headline through the filters — the rows add up to it. */
  summary?: EventGuestArrivals | null;
  /** Only the quarter-hours somebody came in or went out in, in time order. */
  timeline?: EventGuestArrivalTimelinePoint[] | null;
  busiestQuarter?: EventGuestArrivalWindow | null;
  busiestHour?: EventGuestArrivalWindow | null;
  /** Known only for those checked out; `averageMinutes` null when nobody was. */
  stay?: { people?: number | null; averageMinutes?: number | null } | null;
}

/** ROADMAP #179 — what the attendance report is read through; each list narrows it (see `arrivalReport`). */
export interface EventGuestArrivalReportFilters {
  pointIds?: string[];
  /** #179 — only people who LEFT through these points (their last check-out). */
  outPointIds?: string[];
  ticketTypeIds?: string[];
  groupIds?: string[];
}

export interface EventGuestFilter {
  id?: string[] | null;
  /** #169 — checked in at one of these service points (on a recurring event: on the listed date, else any date). */
  checkedInServicePointId?: string[] | null;
  eventId?: string[] | null;
  eventTimeSlotId?: string[] | null;
  /** Guests linked to ANY of these bookings (a booked act's touring party). */
  eventArtistId?: string[] | null;
  personId?: string[] | null;
  rsvpGroupId?: string[] | null;
  rsvpStatus?: string[] | null;
  guestRole?: string[] | null;
  guestCode?: string[] | null;
  /** Guests assigned to ANY of these groups. */
  guestGroupId?: string[] | null;
  /** Guests attending ANY of these time slots (a recurring occurrence / agenda session). */
  attendsTimeSlotId?: string[] | null;
  /** ROADMAP A4: attach each guest's `dateAttendance` for this date (does not narrow the list). */
  attendanceForTimeSlotId?: string | null;
  /** Only counted heads (true) — the Guests list uses this instead of a role filter. */
  isAttendee?: boolean | null;
  /** Attended: true = checked in at the door (checked_in_at set), false = not yet. */
  checkedIn?: boolean | null;
  /** ROADMAP #177 — not arrived / arrived / still here / left, on either rail (their own stamp, or an admission ticket). */
  arrival?: GuestArrivalFilter | null;
  /** ROADMAP #177 — 'latest': latest arrival first (the Arrivals page); the sort orders the ties. */
  arrivalOrder?: "latest" | null;
  /** #286 — the attendees, and the buyers holding a headcount order's live tickets (the Arrivals list's people). */
  atTheDoor?: boolean | null;
  /** #40. true = only guests holding an unpaid restored place; false = exclude them (the review queue). */
  awaitingPayment?: boolean | null;
  /** Narrow to specific lifecycle statuses (omit = active + disabled). */
  status?: number[] | null;
  /** Search over first/last name, email, phone, and guest code. */
  searchKeyword?: string | null;
}

export interface EventGuestWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Derived helpers
// =============================================================================

// Effective field = the guest's own per-event override, else the linked contact's
// live value. So updating a contact flows to every event they attend.
export const getGuestFirstName = (g: EventGuest): string => (g.firstName || g.person?.firstName || "").trim();
export const getGuestLastName = (g: EventGuest): string => (g.lastName || g.person?.lastName || "").trim();
export const getGuestEmail = (g: EventGuest): string | null =>
  (g.email || g.person?.email || "").trim() || null;
export const getGuestPhone = (g: EventGuest): string | null =>
  (g.phone || g.person?.phone || "").trim() || null;

/** Effective bio: the guest's event-specific bio, else the linked contact's. */
export const getGuestBio = (g: EventGuest): string | null =>
  (g.bio || g.person?.bio || "").trim() || null;

/** Effective website: the guest's event-specific website, else the contact's. */
export const getGuestWebsite = (g: EventGuest): string | null =>
  (g.website || g.person?.personalWebsite || "").trim() || null;

/**
 * Effective social links for a guest: each of the three slots resolves to the
 * guest's event-specific value, else the linked contact's. Blank slots are
 * dropped, so the result is a compact list of non-empty URLs.
 */
export const getGuestSocialLinks = (g: EventGuest): string[] =>
  [
    g.socialUrl1 || g.person?.socialUrl1,
    g.socialUrl2 || g.person?.socialUrl2,
    g.socialUrl3 || g.person?.socialUrl3,
  ]
    .map((u) => (u || "").trim())
    .filter(Boolean);

/** A guest's display name from the effective parts, falling back to email / phone / code. */
export const getGuestDisplayName = (guest: EventGuest): string => {
  const name = [
    guest.firstName || guest.person?.firstName,
    guest.middleName || guest.person?.middleName,
    guest.lastName || guest.person?.lastName,
  ]
    .map((p) => (p || "").trim())
    .filter(Boolean)
    .join(" ");

  return name || getGuestEmail(guest) || getGuestPhone(guest) || guest.guestCode || "—";
};

/** Human label for a guest role. */
export const getGuestRoleLabel = (role?: string | null): string =>
  (role && EVENT_GUEST_ROLE_LABELS[role]) || "Guest";

/** Human label for an RSVP status. */
export const getRsvpStatusLabel = (status?: string | null): string =>
  (status && EVENT_RSVP_STATUS_LABELS[status]) || "—";

// =============================================================================
// Service
// =============================================================================

/** One RSVP-able time slot with its attendance (host/crew view). */
export interface EventGuestTimeSlotAttendance {
  id: string;
  name?: string | null;
  /** UTC start/end (render in the event timezone). */
  startsAt?: string | null;
  endsAt?: string | null;
  isAllDay?: boolean | null;
  track?: string | null;
  capacity?: number | null;
  count?: number | null;
  /** Guest ids attending this slot — the list derives per-guest badges from these. */
  guestIds?: string[] | null;
  /** ROADMAP A4: guests checked in on this date (recurring events check in per date). */
  checkedInCount?: number | null;
}

export interface EventTimeSlotAttendance {
  eventId?: string | null;
  /** single | recurring | agenda — labels the selector (Occurrence vs Session). */
  scheduleMode?: string | null;
  slots?: EventGuestTimeSlotAttendance[] | null;
}

const TIME_SLOT_ATTENDANCE_QUERY = gql`
  query EventGuestTimeSlotAttendance($eventId: ID!) {
    eventGuestTimeSlotAttendance(eventId: $eventId) {
      code
      errors { name errors warnings }
      data {
        eventId
        scheduleMode
        slots {
          id
          name
          startsAt
          endsAt
          isAllDay
          track
          capacity
          count
          guestIds
          checkedInCount
        }
      }
    }
  }
`;

export class EventGuestService {
  static async list(
    filter?: EventGuestFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /** ROADMAP #177 — `list` with each guest's `arrival` (the guest list and its export only — see GUEST_ARRIVAL_FIELDS). */
  static async listWithArrival(filter?: EventGuestFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_WITH_ARRIVAL_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /** `list` with each guest's `ticketCount` (the Guests list and its export only). */
  static async listWithTicketCount(filter?: EventGuestFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_WITH_TICKET_COUNT_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * ROADMAP #177 — "Arrived N of M (P%)", in people, free on every plan. Narrowed like the Arrivals list: `groupIds` (every
   * number), `pointIds` (the arrivals through those entrances; `notArrived` comes back null).
   */
  static async arrivals(
    eventId: string,
    eventTimeSlotId?: string | null,
    narrow: { groupIds?: string[]; pointIds?: string[] } = {}
  ): Promise<OpResult> {
    return ApiService.executeQuery(
      ARRIVALS_QUERY,
      {
        params: {
          eventId,
          eventTimeSlotId: eventTimeSlotId ?? null,
          ...(narrow.groupIds?.length ? { guestGroupId: narrow.groupIds } : {}),
          ...(narrow.pointIds?.length ? { checkedInServicePointId: narrow.pointIds } : {}),
        },
      },
      { fetchPolicy: "network-only" }
    );
  }

  /**
   * ROADMAP #178 — the arrivals split by entrance, ticket type, or the groups of one set (`groupSetId`); the rows add up to
   * `arrivals`. On Analytics & reports (`insights`): the server refuses it without (the widget shows the lock first).
   */
  static async arrivalBreakdown(
    eventId: string,
    eventTimeSlotId: string | null | undefined,
    by: GuestArrivalBreakdownBy,
    groupSetId?: string | null
  ): Promise<OpResult> {
    return ApiService.executeQuery(
      ARRIVAL_BREAKDOWN_QUERY,
      {
        params: {
          eventId,
          eventTimeSlotId: eventTimeSlotId ?? null,
          by,
          ...(by === GUEST_ARRIVAL_BREAKDOWN.GROUP ? { guestGroupSetId: groupSetId ?? null } : {}),
        },
      },
      { fetchPolicy: "network-only" }
    );
  }

  /**
   * ROADMAP #179 — the attendance report: the breakdown, the headline, arrivals and departures per quarter-hour, the busiest
   * stretch and the average stay — all on the server. Filters: `groupIds` / `ticketTypeIds` narrow WHO is counted (a type
   * filter leaves out guests holding no ticket); `pointIds` narrows the ARRIVALS (expected stays everyone); `outPointIds`
   * to the people who LEFT through those points (none of them still here). On
   * `insights`: the server refuses it without.
   */
  static async arrivalReport(
    eventId: string,
    eventTimeSlotId: string | null | undefined,
    by: GuestArrivalBreakdownBy,
    groupSetId: string | null | undefined,
    filters: EventGuestArrivalReportFilters = {}
  ): Promise<OpResult> {
    return ApiService.executeQuery(
      ARRIVAL_REPORT_QUERY,
      {
        params: {
          eventId,
          eventTimeSlotId: eventTimeSlotId ?? null,
          by,
          ...(by === GUEST_ARRIVAL_BREAKDOWN.GROUP ? { guestGroupSetId: groupSetId ?? null } : {}),
          ...(filters.pointIds?.length ? { servicePointId: filters.pointIds } : {}),
          ...(filters.outPointIds?.length ? { checkedOutServicePointId: filters.outPointIds } : {}),
          ...(filters.ticketTypeIds?.length ? { ticketTypeId: filters.ticketTypeIds } : {}),
          ...(filters.groupIds?.length ? { guestGroupId: filters.groupIds } : {}),
        },
      },
      { fetchPolicy: "network-only" }
    );
  }

  /** Per-time-slot attendance (counts + rosters) for the guest list's slot selector. */
  static async timeSlotAttendance(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(TIME_SLOT_ATTENDANCE_QUERY, { eventId }, { fetchPolicy: "network-only" });
  }

  /**
   * Fetches every guest matching `filter` by paging through the list. Used for
   * CSV export and the "clear all guests" action. Paged by `listAllPages`: above 50,000 rows it REFUSES rather than
   * cuts, unless `maxRows` states a stop (ROADMAP #118).
   */
  static async listAll(
    filter?: EventGuestFilter,
    sortBy?: SortItem[],
    opts?: ListAllOptions
  ): Promise<OpResult> {
    return listAllPages<EventGuest>(EventGuestService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(GET_MANY_QUERY, { ids });
  }

  static async create(params: EventGuestInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(where: EventGuestWhereInput, params: EventGuestInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }

  /**
   * Mass-changes the bulk-writable fields on every selected guest in one call
   * (the server restricts a multi-id update to that subset). Pass only the fields
   * to change.
   */
  static async bulkUpdate(ids: string[], params: EventGuestBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected guest in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id: ids } });
  }

  /**
   * Door check-in scan on one guest. Non-clobbering server-side: a repeat check-in
   * keeps the ORIGINAL who + when. Look the guest up first (by code or name), then
   * pass its id here.
   */
  static async scan(
    id: string,
    action: GuestScanAction,
    opts: { eventTimeSlotId?: string | null; overrideDate?: boolean; servicePointId?: string | null } = {},
  ): Promise<OpResult> {
    return ApiService.executeMutation(SCAN_MUTATION, {
      params: {
        id,
        action,
        // #169 — the point the scanner stands at (an entrance, a desk), recorded with the stamp.
        ...(opts.servicePointId ? { servicePointId: opts.servicePointId } : {}),
        // ROADMAP A4 — a recurring event checks guests in to ONE date, and refuses a date that is not today or
        // not booked unless crew override (field `overrideDate`).
        ...(opts.eventTimeSlotId ? { eventTimeSlotId: opts.eventTimeSlotId } : {}),
        ...(opts.overrideDate === true ? { overrideDate: true } : {}),
      },
    });
  }

  /**
   * Crew RSVP change — set only a guest's day-of RSVP state (status / party size /
   * rsvp-group headcount / attending), never their identity/role. This is the write
   * path a crew member with `guests.rsvp` (but not `guests.edit`) uses — e.g. door
   * staff changing a guest's status at check-in. Owners + full-edit crew may use it too.
   * Pass one id or several (all must belong to the same event).
   */
  static async setRsvp(
    id: string | string[],
    changes: { rsvpStatus?: string; partySize?: number; rsvpGroupSize?: number; isAttendee?: boolean },
  ): Promise<OpResult> {
    return ApiService.executeMutation(SET_RSVP_MUTATION, { params: { id, ...changes } });
  }

  /**
   * Record the outcome of ONE review — approvals and denials together, so a party judged in one pass
   * is one decision and every contactable guest can be emailed a breakdown of what happened to their
   * whole party. Stamps the decision audit server-side. `reason` describes the DENIAL: it is stored
   * on the denied guests only, and reaches them only when `shareReason` is true.
   *
   * Non-clobbering: a guest already in the target state keeps their original decision and is not
   * re-emailed, so a repeated approve is safe.
   */
  static async decide(params: {
    approveIds?: string[];
    denyIds?: string[];
    reason?: string | null;
    shareReason?: boolean;
  }): Promise<OpResult> {
    return ApiService.executeMutation(DECIDE_MUTATION, {
      params: {
        approveIds: params.approveIds ?? [],
        denyIds: params.denyIds ?? [],
        reason: params.reason?.trim() || null,
        shareReason: !!params.shareReason,
      },
    });
  }

  /**
   * ROADMAP #36 — offer freed places to the waitlist. With `ids`, offers to those guests
   * specifically; with none, fills whatever is free from the front of the queue. Naming guests
   * chooses WHO gets a place, not WHETHER one is free — the server still checks availability, so
   * this can fail with "the event is still full", which is a legitimate answer, not a bug.
   */
  static async waitlistOffer(params: { eventId: string; ids?: string[] }): Promise<OpResult> {
    return ApiService.executeMutation(WAITLIST_OFFER_MUTATION, {
      params: { eventId: params.eventId, ids: params.ids ?? [] },
    });
  }

  /**
   * Guest-list statistics for one event — the totals and the RSVP breakdown, computed server-side.
   *
   * Deliberately an aggregate rather than counting a fetched list on the client: a long guest list
   * would be paged (so the count would be wrong) and the client cannot see the ticket side at all,
   * which is half of what fills the room.
   */
  /**
   * ROADMAP #38 — add crew from the account's own ms_auth users (not the address book).
   *
   * Idempotent: a user already on the event is linked to their account user rather than duplicated,
   * so re-running it after adding someone from Contacts is the intended way to connect them.
   */
  static async addCrewFromUsers(params: { eventId: string; userIds: string[]; guestRole: string }): Promise<OpResult> {
    return ApiService.executeMutation(ADD_CREW_FROM_USERS_MUTATION, { params });
  }

  /** #62 P6c — how many are on the list and how many people it is expected to bring (see GUEST_LIST_SIZE_QUERY). */
  static async listSize(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(GUEST_LIST_SIZE_QUERY, { params: { eventId, eventTimeSlotId: null } }, { fetchPolicy: "network-only" });
  }

  /** ROADMAP #60 — the plan's people limit as the host sees it (see GUEST_LIMIT_QUERY). */
  static async guestLimit(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(GUEST_LIMIT_QUERY, { params: { eventId } }, { fetchPolicy: "network-only" });
  }

  static async stats(params: { eventId: string; eventTimeSlotId?: string | null }): Promise<OpResult> {
    return ApiService.executeQuery(
      GUEST_STATS_QUERY,
      { params: { eventId: params.eventId, eventTimeSlotId: params.eventTimeSlotId ?? null } },
      { fetchPolicy: "network-only" }
    );
  }

  /** ROADMAP #186 b — the dashboard's Tickets sold and RSVPs numbers in one read (see DASHBOARD_STATS_QUERY). */
  static async dashboardStats(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(DASHBOARD_STATS_QUERY, { params: { eventId } }, { fetchPolicy: "network-only" });
  }

  /** Assign bib numbers to the event's guests, per their guest group's config. */
  static async assignBibs(params: GuestAssignBibsInput): Promise<OpResult> {
    return ApiService.executeMutation(ASSIGN_BIBS_MUTATION, { params });
  }

  /**
   * ROADMAP #171 — set these guests' BIB numbers in one save (the door's party drawer). All or nothing: a number
   * someone else on the event holds is refused, naming them, keyed `items[<index>].bibNumber`. Owner or check-in crew.
   */
  static async setBibs(eventId: string, items: BibItemInput[]): Promise<OpResult> {
    return ApiService.executeMutation(SET_BIBS_MUTATION, { params: { eventId, items } });
  }

  /**
   * Randomly + evenly distribute a set of guests across the groups of a chosen guest-group set
   * (capacity-aware, using as many groups as possible). Pass `dryRun: true` to PREVIEW the split
   * without writing any memberships. data[0] is a GuestGroupAssignmentSummary.
   */
  static async assignRandomGroups(params: AssignRandomGroupsInput): Promise<OpResult> {
    return ApiService.executeMutation(ASSIGN_RANDOM_GROUPS_MUTATION, { params });
  }

  /**
   * Send invitation emails (each with a personal RSVP link) to a set of the event's guests.
   * Pass `dryRun: true` to PREVIEW the recipient breakdown without minting tokens, sending, or
   * changing any status. data[0] is an InvitationSendResult.
   */
  static async sendInvitations(params: SendInvitationsInput): Promise<OpResult> {
    return ApiService.executeMutation(SEND_INVITATIONS_MUTATION, { params });
  }

  /**
   * Ensure + return a guest's personal access token, so the host can copy their RSVP link
   * (`…/e/<slug>?inv=<token>`) — the host-issued path for cross-device RSVP restore. data[0]
   * is `{ token, eventId }`.
   */
  static async ensureInviteLink(eventGuestId: string): Promise<OpResult> {
    return ApiService.executeMutation(ENSURE_INVITE_LINK_MUTATION, { eventGuestId });
  }

  /**
   * Owner: set a crew member's access — crew sign-in password (hashed server-side),
   * optional linked account user, and ensure their invite token. Returns
   * { token, eventId, hasPassword }. Pass clearPassword to remove the password.
   */
  static async setCrewAccess(params: {
    eventGuestId: string;
    password?: string | null;
    clearPassword?: boolean;
    userId?: string | null;
  }): Promise<OpResult> {
    return ApiService.executeMutation(SET_CREW_ACCESS_MUTATION, { params });
  }

  /**
   * Send an event-update email (host-authored message, neutral template) to a set of the event's
   * guests. Same input/result as invitations, but the message is required and NO rsvp-status change
   * happens. Pass `dryRun: true` to preview the recipient breakdown.
   */
  static async sendEventUpdate(params: SendInvitationsInput): Promise<OpResult> {
    return ApiService.executeMutation(SEND_UPDATE_MUTATION, { params });
  }

  /**
   * #132 — hand a whole pasted list to the server. Answers as soon as it is stored, with the job (`BulkJob`, kind
   * `guest_import`); the guests are added in the background, and `BulkJobService` reads its progress and report.
   */
  static async startImport(params: { eventId: string; recipients: ImportGuestRecipient[] }): Promise<OpResult> {
    return ApiService.executeMutation(IMPORT_START_MUTATION, { params });
  }

  /**
   * #62 P6c Phase B — what the import WOULD do with each recipient (create / match / attach to a family's invitation),
   * writing nothing. The review says where each row goes before the host adds them.
   * ⚠ #132 — a dry run of at most `GUEST_IMPORT_PLAN_MAX_ROWS` recipients (the server refuses more). The import itself
   *   runs in the background: `startImport` (a `BulkJob`).
   */
  static async planImport(params: ImportGuestsInput): Promise<OpResult> {
    return ApiService.executeMutation(IMPORT_PLAN_MUTATION, { params: { ...params, dryRun: true } });
  }

  /**
   * #62 P6c Phase B — how many of a family's OTHER adults a send would also reach (a dry run; nothing is sent). Its own
   * operation so the main preview does not depend on the new field (see SEND_LINKED_PREVIEW_MUTATION).
   */
  static async previewLinkedAdults(params: SendInvitationsInput, mode: "invitation" | "event_update"): Promise<OpResult> {
    return ApiService.executeMutation(mode === "event_update" ? SEND_UPDATE_LINKED_PREVIEW_MUTATION : SEND_LINKED_PREVIEW_MUTATION, {
      params: { ...params, dryRun: true },
    });
  }

  /** Render the invitation email as ONE guest would see it (subject + html). Nothing is sent. */
  /**
   * What this event's sends can draw on — allowance AND purchased credits.
   *
   * ⚠ The EVENT OWNER's balance, not the caller's — crew can send on someone else's event and
   * the send is metered against the owner. ⚠ `network-only`: this is polled while waiting for
   * a purchase to land, and a cached answer would say "not yet" forever.
   */
  static async messagingBalance(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(
      MESSAGING_BALANCE_QUERY,
      { params: { eventId } },
      { fetchPolicy: "network-only" }
    );
  }

  static async previewInvitation(params: PreviewInvitationInput): Promise<OpResult> {
    return ApiService.executeQuery(PREVIEW_INVITATION_QUERY, { params }, { fetchPolicy: "network-only" });
  }

  /** The event-update SCENARIO catalog for an event + channel ('email' | 'sms'). */
  static async eventUpdateCatalog(eventId: string, channel?: string): Promise<OpResult> {
    return ApiService.executeQuery(UPDATE_CATALOG_QUERY, { eventId, channel }, { fetchPolicy: "network-only" });
  }

  /** The invitation message catalog for an event + channel ('email' | 'sms'); email auto-picks by occasion. */
  static async invitationCatalog(eventId: string, channel?: string): Promise<OpResult> {
    return ApiService.executeQuery(INVITATION_CATALOG_QUERY, { eventId, channel }, { fetchPolicy: "network-only" });
  }
}

export interface GuestAssignBibsInput {
  eventId: string;
  /** registration | name | random */
  sortOrder: BibSortOrder;
  /** Renumber guests that already have a bib too (default false = fill blanks). */
  overwrite?: boolean;
}

/** keep = only place audience guests not yet in a group of the set; reshuffle = clear + redistribute all of them. */
export type GuestGroupAssignMode = "keep" | "reshuffle";

export interface AssignRandomGroupsInput {
  eventId: string;
  /** The guest-group set (axis) to fill — this event's own or an account-wide reusable set. */
  guestGroupSetId: string;
  /** The audience — guest ids to spread across the set's groups (resolve 'all' / filters / selection to ids on the client). */
  eventGuestIds: string[];
  /** keep (default) | reshuffle. */
  mode?: GuestGroupAssignMode;
  /** Preview only: compute + report the split without writing memberships. */
  dryRun?: boolean;
}

export interface GuestGroupAssignmentGroup {
  guestGroupId?: string;
  name?: string | null;
  /** The group's capacity cap (null = unlimited). */
  maxMembers?: number | null;
  /** How many audience guests this run placed into the group. */
  assigned?: number;
  /** The group's final member count (existing kept + newly assigned). */
  total?: number;
}

export interface GuestGroupAssignmentSummary {
  eventId?: string;
  guestGroupSetId?: string;
  mode?: GuestGroupAssignMode;
  dryRun?: boolean;
  /** Assignable audience size (active guests resolved from the requested ids). */
  audienceTotal?: number;
  /** How many memberships this run created. */
  placed?: number;
  /** Audience guests skipped because already in a group of this set (keep mode). */
  alreadyAssigned?: number;
  /** Audience guests that could not be placed because every group was at capacity. */
  unplaced?: number;
  /** Per-group breakdown, in set order. */
  groups?: GuestGroupAssignmentGroup[];
}

export interface SendInvitationsInput {
  eventId: string;
  /** The guest ids to invite (resolve 'all' / filters to ids on the client). */
  eventGuestIds: string[];
  /** Public events-web origin for the invite link, e.g. https://app.eventsmagic.com. Falls back to the configured web app url. */
  siteOrigin?: string | null;
  /** An optional personal note from the host, shown in the invitation body. */
  message?: string | null;
  /** Body format: 'html' (WYSIWYG rich text) or 'plain'. Defaults to plain server-side. */
  messageFormat?: string | null;
  /** Delivery channel: 'email' (default) or 'sms'. SMS sends a plain text body to phones and requires the link token. */
  channel?: string | null;
  /** The chosen catalog template id — ONLY used by the WhatsApp channel (each catalog entry is its own approved Content template). Ignored by email/SMS. */
  templateId?: string | null;
  /** Preview only: resolve recipients + report the breakdown without minting/sending/status changes. */
  dryRun?: boolean;
}

/** One destination zone's share of a send's RECIPIENTS (⚠ people, not allowance units). */
export interface InvitationSendZoneCost {
  zone?: string | null;
  recipients?: number | null;
}

export interface InvitationSendResult {
  eventId?: string;
  dryRun?: boolean;
  recipientsTotal?: number;
  withEmail?: number;
  withoutEmail?: number;
  /** #62 P6c Phase B — extra copies to a family's other adults (read through `previewLinkedAdults` only). */
  linkedAdults?: number | null;
  sent?: number;
  failed?: number;
  tokensMinted?: number;
  /** Email allowance units this send costs — one per recipient with an email. */
  costEmails?: number | null;
  /**
   * Message allowance units this send costs.
   *
   * ⚠ NOT one per recipient: a long body and a distant destination each multiply it, which is
   * exactly why the host has to see this number BEFORE the button rather than on a statement.
   */
  costMessages?: number | null;
  /** Recipients per destination zone, so the composer can say "248 North America, 64 Europe". */
  costByZone?: InvitationSendZoneCost[] | null;
  /**
   * How many units EACH recipient costs, from the body's length and encoding.
   *
   * ⚠ Say "counts as N per person". NEVER the word "segment" — it is our word, it means
   * nothing to a host, and it is the reason variable pricing reads as a trap.
   */
  countsPerRecipient?: number | null;
  /**
   * The character that flipped the body to the expensive encoding — an emoji, a curly
   * apostrophe. Null when nothing did. Name it: "the 🎉 makes each message count double" is
   * actionable in a way that "your message is long" is not.
   */
  expensiveCharacter?: string | null;
}

export interface PreviewInvitationInput {
  eventId: string;
  eventGuestId: string;
  siteOrigin?: string | null;
  message?: string | null;
  /** Body format: 'html' (WYSIWYG rich text) or 'plain'. */
  messageFormat?: string | null;
  /** Which template to render: 'invitation' (default) or 'event_update'. */
  mode?: string | null;
  /** Delivery channel: 'email' (default) renders HTML; 'sms' returns the plain text body in `text`. */
  channel?: string | null;
}

export interface InvitationPreview {
  eventGuestId?: string;
  guestName?: string | null;
  subject?: string | null;
  html?: string | null;
  text?: string | null;
}

export interface InvitationCatalogMessage {
  id?: string;
  label?: string | null;
  eventType?: string | null;
  tone?: string | null;
  body?: string | null;
}

export interface InvitationCatalog {
  recommendedId?: string | null;
  eventCategoryCode?: string | null;
  messages?: InvitationCatalogMessage[] | null;
}

export interface ImportGuestRecipient {
  firstName?: string | null;
  lastName?: string | null;
  /** Email and/or phone identifies the recipient — at least one is required. */
  email?: string | null;
  /** Phone in any format — normalized to E.164 server-side. A recipient may have email and/or phone. */
  phone?: string | null;
  /** Raw pasted label ("Damons Family", "John & Anna") stored as the guest's RSVP-group name. */
  groupName?: string | null;
  /** Per-guest cap on how many people this guest may RSVP for (their household). */
  rsvpGroupSize?: number | null;
  /**
   * #62 P2b — an ADDRESS-BOOK pick: the contact to invite (no email or phone needed). With `groupName` and a group
   * size of 2+ the server sets up (or reuses) the contact's household.
   */
  personId?: string | null;
  /**
   * #62 P6c Phase B — the OTHER adults of this recipient's family, each to get their own link to the SAME invitation.
   * ⚠ Sent only when there are some (`toImportRecipient`): an un-recomposed gateway rejects an unknown input field.
   */
  alsoNotifyPersonIds?: string[];
}

/** #132 — what became of a pasted recipient. Mirrors `GUEST_IMPORT_ROW_OUTCOME` in the server's `boundary/guestImport.ts`. */
export const GUEST_IMPORT_ROW_OUTCOME = {
  CREATE: "create",
  MATCH: "match",
  ATTACH: "attach",
  SKIP: "skip",
} as const;

/** ROADMAP #60 — the plan's people limit as the host sees it (`eventGuestLimit`). */
export interface EventGuestLimit {
  eventId: string;
  /** People the plan allows on the list; null = unlimited. */
  peopleLimit: number | null;
  /** Spots used by the people the host can see. */
  shown: number | null;
  /** There are MORE people past the limit (never how many). */
  hasHidden: boolean;
  /** The limit applies now — false on a draft or a test run (publishing asks). */
  enforced: boolean;
  /** False when the plan could not be read: say nothing. */
  known: boolean;
}

/** #132 — why a pasted recipient was refused (beside `BULK_JOB_REASON.GAVE_UP`). Mirrors `GUEST_IMPORT_SKIP_REASON` on the server. */
export const GUEST_IMPORT_SKIP_REASON = {
  NO_CONTACT: "no_contact",
  NOT_A_CONTACT: "not_a_contact",
  NOT_SAVED: "not_saved",
  /** ROADMAP #60 — a new guest past the plan's people limit (partylabz Free: 15). */
  PLAN_LIMIT: "plan_limit",
} as const;

/**
 * #132 — the review's dry run covers at most this many rows (the first ones). Mirrors `GUEST_IMPORT_PLAN_MAX_ROWS` on
 * the server, which refuses a longer review.
 */
export const GUEST_IMPORT_PLAN_MAX_ROWS = 200;

export interface ImportGuestsInput {
  eventId: string;
  recipients: ImportGuestRecipient[];
  /** #62 P6c Phase B — plan only, write nothing (use `planImport`). */
  dryRun?: boolean;
}

/** #62 P6c Phase B — what the import does (or would do) with one recipient. */
export interface ImportGuestPlanItem {
  index?: number | null;
  /** create | match | attach | skip — attach = joins an invitation already on the list. */
  outcome?: "create" | "match" | "attach" | "skip" | string | null;
  eventGuestId?: string | null;
  /** Who the invitation it joins is addressed to (the family's name, else the lead's). */
  leadName?: string | null;
  /** For attach: that invitation's group size now (it grows to the row's if bigger). */
  leadSize?: number | null;
  linked?: number | null;
}

export interface ImportGuestsResult {
  eventId?: string;
  created?: number;
  matched?: number;
  skipped?: number;
  guestIds?: string[];
  attached?: number | null;
  linked?: number | null;
  plan?: ImportGuestPlanItem[] | null;
}
